import { useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { useQuota } from 'features/quota/hooks/useQuota';
import { useCompanyQuotaQuery } from 'features/quota/useCompanyQuotaQuery';

import { Header } from '../components/Header';
import { Tooltip } from '../components/Tooltip';
import { EMPTY_QUOTA } from '../domain/quota';
import { useEmailsQuotaQuery } from '../features/insights/useEmailsQuotaQuery';
import { useIsSwUser } from '../hooks/useIsSwUser';
import { appStore } from '../services/appStore';

import { PackageUpgradeBlock } from './settings/PackageUpgradeBlock';
import { QuotaBlock } from './settings/QuotaBlock';
import { SettingsBlock } from './settings/SettingsBlock';
import { SettingsFooter } from './settings/SettingsFooter';
import { useSettingEnabling } from './settings/useSettingEnabling';
import { useSettingsTracking } from './settings/useSettingsTracking';
import {
  StyledArrowButton,
  StyledSettingsHeader,
  StyledSettingsPage,
  StyledSettingsTitle,
  StyledSettingTitleContainer,
} from './styles';

export const SettingsPage = () => {
  const { t } = useTranslation();
  const { handleUpgradePackageClick } = useSettingsTracking();
  const isSwUser = useIsSwUser();
  const tooltipRef = useRef(null);

  const insightsQuota = useEmailsQuotaQuery()?.data ?? EMPTY_QUOTA;
  const companyQuota = useCompanyQuotaQuery().data;

  const { quota } = useQuota();

  const { isSettingApplied } = useSettingEnabling();

  const handleTrialButtonClick = () => {
    handleUpgradePackageClick(insightsQuota.remaining, quota.remaining, companyQuota?.remaining);
  };

  return (
    <div>
      <Header />
      <StyledSettingsPage data-automation="SettingsPage">
        <div>
          <StyledSettingsHeader>
            <Tooltip
              content={t('settings.back.tooltip')}
              enabled={isSettingApplied}
              placement="bottom-right"
              minWidth={230}
              visible={isSettingApplied}
              contentRef={tooltipRef}
              withOutsideClick
            >
              <StyledArrowButton onClick={() => appStore.resetSettingsPage()} variant="flat">
                <SWReactIcons iconName="arrow-left" iconColor={colorsPalettes.carbon[500]} size="xs" />
              </StyledArrowButton>
            </Tooltip>
            {t('header.settings')}
          </StyledSettingsHeader>
          <StyledSettingsTitle>{t('settings.preferences')}</StyledSettingsTitle>
          <SettingsBlock />
          <StyledSettingTitleContainer>
            <StyledSettingsTitle>{t('settings.credits')}</StyledSettingsTitle>
            {!isSwUser ? <PackageUpgradeBlock onTrialButtonClick={handleTrialButtonClick} /> : null}
          </StyledSettingTitleContainer>

          <QuotaBlock insightsQuota={insightsQuota} companyQuota={companyQuota} quota={quota} />
        </div>
        <SettingsFooter />
      </StyledSettingsPage>
    </div>
  );
};
